import clsx from 'clsx';
import type { HTMLAttributes } from 'react';
import type { Product } from '../api';
import { formatPrice } from '../utils';

export type PriceDetailProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> &
  Partial<
    Pick<Product, 'listPrice' | 'salePrice' | 'pointPrice' | 'pointPriceName'>
  > & {
    size?: 'small' | 'medium' | 'large';
    overturn?: boolean;
    disabled?: boolean;
  };

const PriceFontClassMap: Record<
  NonNullable<PriceDetailProps['size']>,
  Record<'last' | 'sale' | 'point', string>
> = {
  large: {
    last: 'tlw-txt-price-3',
    sale: 'tlw-txt-price-1',
    point: 'tlw-txt-zh-body-2',
  },
  medium: {
    last: 'tlw-txt-price-mini',
    sale: 'tlw-txt-price-2',
    point: 'tlw-txt-zh-mini',
  },
  small: {
    last: 'tlw-txt-price-mini',
    sale: 'tlw-txt-price-2',
    point: 'tlw-txt-zh-mini',
  },
};

function generateDiscountInfo(
  pointPrice: PriceDetailProps['pointPrice'],
  pointPriceName: PriceDetailProps['pointPriceName']
) {
  if (!pointPrice || !pointPriceName) return null;
  const [point, price] = pointPrice.split('+').map((item) => item.trim());
  const description = `${pointPriceName + point} +`;
  return { description, price };
}

export function PriceDetail({
  listPrice: propListPrice,
  salePrice: propSalePrice,
  pointPrice,
  pointPriceName,
  size = 'medium',
  overturn,
  disabled,
  ...props
}: PriceDetailProps) {
  const priceFontClassNames = PriceFontClassMap[size];

  /** 原價 */
  const listPrice = propListPrice && formatPrice(propListPrice);
  /** 優惠價 */
  const salePrice = propSalePrice && formatPrice(propSalePrice);
  /** 點數優惠信息 */
  const discountInfo = generateDiscountInfo(pointPrice, pointPriceName);

  /** 是否為區間價 */
  const isIntervalPrice = Boolean(listPrice?.includes('-'));
  /** 是否展示原價 */
  const isLastPriceVisible = salePrice !== listPrice;
  /** 是否展示優惠 */
  const isSalePriceVisible = Boolean(salePrice);

  return (
    <div {...props}>
      {/* 售價 */}
      <p
        className={clsx(
          'tlw-flex',
          isIntervalPrice
            ? [overturn ? 'tlw-flex-col' : 'tlw-flex-col-reverse']
            : 'tlw-items-center tlw-gap-2',
          disabled && 'tlw-text-txt-500'
        )}
      >
        {/* 優惠價 */}
        {isSalePriceVisible && (
          <span
            className={clsx(
              priceFontClassNames.sale,
              !disabled && 'tlw-text-txt-core'
            )}
          >
            {salePrice}
          </span>
        )}

        {/* 原價 */}
        {isLastPriceVisible && (
          <span
            className={clsx(
              isSalePriceVisible
                ? [
                    `${priceFontClassNames.last} tlw-line-through`,
                    !disabled && 'tlw-text-txt-600',
                  ]
                : [priceFontClassNames.sale, !disabled && 'tlw-text-txt-800']
            )}
          >
            {listPrice}
          </span>
        )}
      </p>

      {/*  點數優惠價  */}
      {discountInfo && (
        <p className="tlw-flex tlw-items-center tlw-gap-2">
          <span className={`${priceFontClassNames.point} tlw-text-txt-800`}>
            {discountInfo.description}
          </span>

          <span className={`${priceFontClassNames.sale} tlw-text-txt-core`}>
            {discountInfo.price}
          </span>
        </p>
      )}
    </div>
  );
}
